






































import { Product, Sensor, Site, Transceiver, Vat } from '@/api';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { VatSensorTypes } from './models/vat-sensor-type';
import LocationRow from '@/components/display/information-rows/location-row.vue';
import { LastCheckInService } from '@/services/last-check-in-service';
import LastCheckInTag from '@/components/display/information-rows/last-check-in-tag.vue';
import moment from 'moment';
import { Constants } from '@/common/Constants';
import LiveAtRow from '@/components/display/information-rows/live-at-row.vue';
import { formattedStatus } from '@/common/StatusUtil';

@Component({
	components: { LastCheckInTag, LocationRow, LiveAtRow },
})
export default class MilkVatGeneralInfo extends Vue {
	@Prop({ required: true, type: Object })
	public readonly product!: Product & { site: Site | undefined };

	@Prop({ required: true, type: Object })
	public readonly vat!: Vat & { transceiver: Transceiver | undefined; sensors: Sensor[] };

	public generalFields: {
		label: string;
		value: string;
		to?: string | { name: string; params: any };
		formatter?: (value: string) => string;
		extra?: any;
	}[] = [];

	public get configurationFields(): { label: string; value: string }[] {
		const { state, formula, sizeString, vatOffset, emptyDistance, temperatureOffset } = this.vat;
		return [
			{ label: 'State', value: Vue.filter('titleCase')(state ?? 'No state') },
			{ label: 'Model', value: formula ?? 'No model' },
			{ label: 'Size', value: sizeString ?? 'No size' },
			{ label: 'Offset', value: vatOffset?.toString() ?? 'No offset' },
			{ label: 'Empty Distance', value: emptyDistance?.toString() ?? 'No empty distance' },
			{
				label: 'Temperature Offset',
				value: temperatureOffset?.toString() ?? 'No temperature offset',
			},
		];
	}

	@Watch('product')
	@Watch('vat')
	public created() {
		this.updateFields();
	}

	private updateFields() {
		const { id, vatNumber, label, status, transceiver } = this.vat;
		this.generalFields = [
			{ label: 'Id', value: id?.toString() ?? 'No Id' },
			{
				label: 'Number | Label',
				value: `${vatNumber ?? 'No vat number'} | ${label ?? 'No Label'}`,
			},
			{
				label: 'Status',
				value: Vue.filter('titleCase')(formattedStatus(this.vat, status, 'entity')),
			},
			{
				label: 'Product',
				value: this.product.name ?? 'No product name',
				to: `/account/products/milk/${this.product.id}`,
			},
			{
				label: 'Site',
				value: this.product.site?.name ?? 'No site name',
				to: this.product.site ? `/account/sites/view/${this.product.site?.id}` : '/',
			},
			{
				label: 'Live At',
				value: '',
			},
			{
				label: 'Transceiver',
				value: transceiver?.serialNumber?.toString() ?? 'No transceiver',
				to: transceiver ? `/inventory/transceivers/${transceiver.id}` : '/',
				extra: {
					entity: transceiver,
					entityType: 'transceiver',
				},
			},
			{
				label: 'Sensor | Volume',
				value: this.volumeSensor?.serialNumber ?? 'No volume sensor',
				to: this.volumeSensor
					? { name: 'sensors.view', params: { sensorId: this.volumeSensor?.id } }
					: undefined,
				extra: {
					entity: this.volumeSensor,
					entityType: 'sensor',
				},
			},
			{
				label: 'Sensor | Stirrer',
				value: this.stirrerSensor?.serialNumber ?? 'No stirrer sensor',
				to: this.stirrerSensor
					? { name: 'sensors.view', params: { sensorId: this.stirrerSensor?.id } }
					: undefined,
				extra: {
					entity: this.stirrerSensor,
					entityType: 'sensor',
				},
			},
			{
				label: 'Sensor | Inlet Temp',
				value: this.inletTempSensor?.serialNumber ?? 'No inlet temp sensor',
				to: this.inletTempSensor
					? { name: 'sensors.view', params: { sensorId: this.inletTempSensor?.id } }
					: undefined,
				extra: {
					entity: this.inletTempSensor,
					entityType: 'sensor',
				},
			},
			{
				label: 'Sensor | Vat Temp',
				value: this.vatTempSensor?.serialNumber ?? 'No vat temp sensor',
				to: this.vatTempSensor
					? { name: 'sensors.view', params: { sensorId: this.vatTempSensor?.id } }
					: undefined,
				extra: {
					entity: this.vatTempSensor,
					entityType: 'sensor',
				},
			},
		];
	}

	private get volumeSensor(): Sensor | undefined {
		return this.vat.sensors.find(
			(sensor: Sensor) =>
				sensor.type === VatSensorTypes.LIDAR || sensor.type === VatSensorTypes.RADAR,
		);
	}

	private get stirrerSensor(): Sensor | undefined {
		return this.vat.sensors.find((sensor: Sensor) => sensor.type === VatSensorTypes.STIRRER);
	}

	private get inletTempSensor(): Sensor | undefined {
		return this.vat.sensors.find((sensor: Sensor) => sensor.type === VatSensorTypes.INLET_TEMP);
	}

	private get vatTempSensor(): Sensor | undefined {
		return this.vat.sensors.find((sensor: Sensor) => sensor.type === VatSensorTypes.VAT_TEMP);
	}
}
